import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

// Right-sized WebP renditions (≤1024 px, 45–100 KB each) bundled with the app
// and served with the hashed-asset cache policy. The originals were 1.3–1.9 MB
// PNGs on media.base44.com; the first one alone was the heaviest request on
// the homepage. The glob is sorted by file name, so the numeric prefix
// (gallery-01 … gallery-30) fixes the order of the captions below.
const GALLERY_IMAGES = Object.entries(
  import.meta.glob('../../assets/home/gallery/gallery-*.webp', { eager: true, import: 'default' })
).sort(([a], [b]) => a.localeCompare(b)).map(([, url]) => url);

const CAPTIONS = [
  { treatment: "Fixed New Teeth", description: "Patient smiling with her before photo on screen — Fixed New Teeth by Trinity Dental Arts, Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Patient smiling after full-arch implant restoration — Fixed New Teeth by Trinity Dental Arts, Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Male patient with full-arch fixed implant teeth, before photo shown on monitor — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient displaying fixed implant smile transformation, before image on screen — Trinity Dental Arts FL" },
  { treatment: "Fixed New Teeth", description: "Male patient smiling after All-on-4 restoration with before photo displayed — Fixed New Teeth Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Patient next to monitor showing dental before photo — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient smiling beside before-and-after monitor display — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Female patient with new fixed implant smile and before photo on screen — Trinity Dental Arts Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Male patient smiling next to monitor with his before photo — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient celebrating fixed new teeth result with before photo displayed — Trinity Dental Arts FL" },
  { treatment: "Fixed New Teeth", description: "Patient smiling after full-arch implant restoration — Fixed New Teeth by Trinity Dental Arts, Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Male patient with fixed implant smile showing before image on monitor — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient next to before photo on screen — Fixed New Teeth by Trinity Dental Arts, Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Patient smiling with before dental photo displayed on ViewSonic monitor — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient showing fixed implant smile transformation alongside before photo — Trinity Dental Arts FL" },
  { treatment: "Fixed New Teeth", description: "Male patient with new smile posing beside before-and-after display — Fixed New Teeth Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Female patient smiling after full-arch fixed implant procedure — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient displaying confident smile after implant-supported full-arch restoration — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Before and after result: patient smiling with fixed implant teeth, before photo on screen — Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Patient celebrating new fixed teeth with before dental photo displayed — Fixed New Teeth Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Male patient smiling after All-on-4 fixed teeth procedure, before photo on monitor — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient with new smile posing next to before photo on screen — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Female patient smiling after full-arch dental implant restoration — Fixed New Teeth Trinity Dental Arts FL" },
  { treatment: "Fixed New Teeth", description: "Patient displaying before dental photo alongside new fixed implant smile — Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Male patient smiling with before photo on monitor — Fixed New Teeth by Trinity Dental Arts, Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Patient next to before-and-after dental display — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient showcasing fixed implant smile with before photo — Trinity Dental Arts Trinity FL" },
  { treatment: "Fixed New Teeth", description: "Female patient smiling beside before dental photo on screen — Fixed New Teeth Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient with radiant new smile, before photo displayed on monitor — Fixed New Teeth by Trinity Dental Arts" },
  { treatment: "Fixed New Teeth", description: "Patient smiling after full-arch implant transformation, before photo on screen — Trinity Dental Arts FL" },
];

const smiles = GALLERY_IMAGES.map((image, index) => ({ image, ...CAPTIONS[index % CAPTIONS.length] }));

export default function SmileGallery() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = React.useRef(null);
  const autoPlayRef = React.useRef(null);
  const sectionRef = React.useRef(null);
  const [isOnScreen, setIsOnScreen] = useState(false);

  const resetAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % smiles.length);
    }, 5000);
  };

  // Autoplay (and therefore image downloads) only while the gallery is on
  // screen and the tab is visible. The section sits far below the fold, so an
  // unconditional 5-second rotation was quietly downloading a new photo every
  // five seconds for the whole visit, including on mobile data.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsOnScreen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      { rootMargin: '200px 0px', threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const shouldPlay = () => isOnScreen && document.visibilityState !== 'hidden';
    const sync = () => {
      if (shouldPlay()) resetAutoPlay();
      else if (autoPlayRef.current) { clearInterval(autoPlayRef.current); autoPlayRef.current = null; }
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      if (autoPlayRef.current) { clearInterval(autoPlayRef.current); autoPlayRef.current = null; }
    };
  }, [isOnScreen]);

  // Warm the next slide shortly before it is shown so the crossfade never
  // reveals a half-loaded image, without fetching the whole set up front.
  useEffect(() => {
    if (!isOnScreen) return undefined;
    const timer = setTimeout(() => {
      const next = new Image();
      next.decoding = 'async';
      next.src = smiles[(currentIndex + 1) % smiles.length].image;
    }, 2500);
    return () => clearTimeout(timer);
  }, [currentIndex, isOnScreen]);

  const goToSlide = (index) => { setCurrentIndex(index); resetAutoPlay(); };
  const nextSlide = () => { setCurrentIndex((prev) => (prev + 1) % smiles.length); resetAutoPlay(); };
  const prevSlide = () => { setCurrentIndex((prev) => (prev - 1 + smiles.length) % smiles.length); resetAutoPlay(); };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? nextSlide() : prevSlide();
    touchStartX.current = null;
  };

  // Show max 10 dot indicators on mobile to prevent overflow
  const visibleDots = smiles.length > 10 ? null : smiles;

  return (
    <section ref={sectionRef} className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200/50 rounded-full mb-4 md:mb-6">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Real Patient Results</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-slate-800 mb-4 md:mb-6">
            Smile <span className="font-serif italic text-amber-700">Gallery</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600">
            See before-and-after photos from Trinity Dental Arts patients. Each case shows that patient’s individual result.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Main Slideshow */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/50 bg-slate-100">
            <div
              className="relative w-full aspect-[4/3] sm:aspect-square"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <AnimatePresence mode="crossFade">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0"
                >
                  <img
                    src={smiles[currentIndex].image}
                    alt={smiles[currentIndex].description}
                    className="w-full h-full object-contain bg-slate-100"
                    width={1024}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                  />
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <button
                onClick={prevSlide}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full shadow-lg transition-all z-10 touch-manipulation"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full shadow-lg transition-all z-10 touch-manipulation"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              {/* Slide Counter */}
              <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-medium">
                {currentIndex + 1} / {smiles.length}
              </div>
            </div>

            {/* Info Section */}
            <div className="bg-white p-4 md:p-8 border-t border-slate-100">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-600" />
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                    {smiles[currentIndex].treatment}
                  </p>
                </div>
                <p className="text-slate-700 text-sm md:text-base leading-relaxed">
                  {smiles[currentIndex].description}
                </p>
              </motion.div>
            </div>
          </div>

          {/* Mobile Progress Bar */}
          <div className="sm:hidden mt-4">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-600 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / smiles.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Dot Indicators — only show on larger screens to prevent overflow */}
          {visibleDots && (
            <div className="hidden sm:flex justify-center gap-2 md:gap-3 mt-6 md:mt-8 flex-wrap">
              {smiles.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2.5 md:h-3 rounded-full transition-all touch-manipulation ${
                    index === currentIndex
                      ? 'bg-amber-600 w-6 md:w-8'
                      : 'bg-slate-300 w-2.5 md:w-3 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          <p className="text-center text-xs md:text-sm text-slate-500 mt-4 md:mt-6">
            Swipe or use arrows to browse patient results.
          </p>
        </motion.div>
      </div>
    </section>
  );
}